import React, { useState, useMemo } from 'react';
import './AddUserModal.css';

const FIXED_ROLES = [
    { label: 'Administrador', appRole: 'admin', customRoleId: null },
    { label: 'Propietario', appRole: 'owner', customRoleId: null },
];

const AddUserModal = ({ isOpen, onClose, onSubmit, loading = false, orgRoles = [], roleError = null, onRoleErrorClear }) => {
    const roleOptions = useMemo(() => {
        const custom = orgRoles.map(r => ({ label: r.name, appRole: 'user', customRoleId: r.id }));
        if (custom.length === 0) {
            custom.push({ label: 'Usuario', appRole: 'user', customRoleId: null });
        }
        return [...custom, ...FIXED_ROLES];
    }, [orgRoles]);

    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        roleIndex: ''
    });
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Clear role API error when user changes selection
        if ((name === 'roleIndex') && roleError && onRoleErrorClear) {
            onRoleErrorClear();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate email
        if (!formData.email.trim()) {
            newErrors.email = 'El email es obligatorio';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Ingresa un email válido';
        }

        // Validate first name
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'El nombre es obligatorio';
        } else if (formData.firstName.trim().length < 2) {
            newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
        }

        // Validate last name
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'El apellido es obligatorio';
        } else if (formData.lastName.trim().length < 2) {
            newErrors.lastName = 'El apellido debe tener al menos 2 caracteres';
        }

        // Validate role
        if (!formData.roleIndex) {
            newErrors.roleIndex = 'El rol es obligatorio';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            const selected = roleOptions[parseInt(formData.roleIndex, 10)] || roleOptions[0];
            onSubmit({
                email: formData.email.trim(),
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                appRole: selected.appRole,
                customRoleId: selected.customRoleId || undefined
            });
        }
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({ email: '', firstName: '', lastName: '', roleIndex: '' });
        setErrors({});
        if (onRoleErrorClear) onRoleErrorClear();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-container">
                <div className="modal-header">
                    <h2>Agregar Usuario</h2>
                    <button
                        onClick={handleClose}
                        className="modal-close-btn"
                        type="button"
                        disabled={loading}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={errors.email ? 'error' : ''}
                            placeholder="usuario@ejemplo.com"
                            disabled={loading}
                        />
                        {errors.email && <span className="error-text">{errors.email}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="firstName">Nombre *</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                className={errors.firstName ? 'error' : ''}
                                placeholder="Nombre"
                                disabled={loading}
                            />
                            {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="lastName">Apellido *</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                className={errors.lastName ? 'error' : ''}
                                placeholder="Apellido"
                                disabled={loading}
                            />
                            {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="roleIndex">Rol *</label>
                        <select
                            id="roleIndex"
                            name="roleIndex"
                            value={formData.roleIndex}
                            onChange={handleInputChange}
                            className={(errors.roleIndex || roleError) ? 'error' : ''}
                            disabled={loading}
                        >
                            <option value="" disabled>Selecciona un rol</option>
                            {roleOptions.map((opt, idx) => (
                                <option key={idx} value={String(idx)}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {(errors.roleIndex || roleError) && (
                            <span className="error-text">{errors.roleIndex || roleError}</span>
                        )}
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="btn-cancel"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Agregando...
                                </>
                            ) : (
                                'Agregar Usuario'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddUserModal;